export function initializeChatPage() {
    // Helper function to get a cookie by name
    function getCookie(name) {
        let cookieValue = null;
        if (document.cookie && document.cookie !== '') {
            const cookies = document.cookie.split(';');
            for (let i = 0; i < cookies.length; i++) {
                const cookie = cookies[i].trim();
                if (cookie.substring(0, name.length + 1) === (name + '=')) {
                    cookieValue = decodeURIComponent(cookie.substring(name.length + 1));
                    break;
                }
            }
        }
        return cookieValue;
    }

    // Helper function to render formatted content (Markdown + LaTeX)
    function renderFormattedContent(element) {
        if (window.renderMathInElement) {
            try {
                window.renderMathInElement(element, {
                    delimiters: [
                        {left: '$$', right: '$$', display: true},
                        {left: '\\[', right: '\\]', display: true},
                        {left: '$', right: '$', display: false},
                        {left: '\\(', right: '\\)', display: false}
                    ],
                    throwOnError: false
                });
            } catch (error) {
                console.error("KaTeX rendering error:", error);
            }
        }
    }

    function getResourceId(resource) {
        if (!resource) return null;
        if (typeof resource._id === 'object' && resource._id !== null && resource._id.$oid) {
            return resource._id.$oid;
        }
        return resource.id || resource._id || resource._key;
    }

    // Helper for consistent ID extraction
    function getItemId(item) {
        return getResourceId(item);
    }

    // === DEV_TASKS-0036: Phase 2.1 Implement Popover Helpers ===
    function renderPopoverList(data, currentId, type) {
        if (!data || data.length === 0) {
            return `<div class="p-3 text-muted">No ${type === 'prompt' ? 'personas' : 'LLMs'} available.</div>`;
        }

        let html = '<div class="popover-menu">';
        
        // Specific handling for LLM settings if it includes Mode
        if (type === 'llm') {
            html += '<div class="px-3 pt-2"><h6>Language Model</h6></div>';
        }

        data.forEach(item => {
            const itemId = getItemId(item);
            const isActive = String(itemId) === String(currentId);
            
            // Clean/Sanitize data
            const name = DOMPurify.sanitize(item.name || 'Unknown');
            const desc = DOMPurify.sanitize(item.description || '');

            html += `
                <button type="button" class="popover-list-item ${isActive ? 'active' : ''}" data-id="${itemId}" data-type="${type}">
                    <span class="item-name">${name}</span>
                    <span class="item-desc">${desc}</span>
                </button>
            `;
        });

        if (type === 'llm') {
            html += `
                <div class="px-3 pt-2 pb-1 border-top mt-2"><h6>Chat Mode</h6></div>
                <button type="button" class="popover-list-item ${window.chatState.mode === 'analysis' ? 'active' : ''}" data-mode="analysis">
                    <span class="item-name">Focused Inquiry</span>
                    <span class="item-desc">Analyze specific questions deeply</span>
                </button>
                <button type="button" class="popover-list-item ${window.chatState.mode === 'conversation' ? 'active' : ''}" data-mode="conversation">
                    <span class="item-name">Conversation</span>
                    <span class="item-desc">Free-flowing chat with history</span>
                </button>
            `;
        }

        html += '</div>';
        return html;
    }

    function initSelectionPopover(triggerEl, data, currentIdGetter, type, onSelect) {
        if (!triggerEl) return;

        // Dispose existing instance
        const existingPopover = bootstrap.Popover.getInstance(triggerEl);
        if (existingPopover) existingPopover.dispose();

        const popover = new bootstrap.Popover(triggerEl, {
            html: true,
            sanitize: false,
            trigger: 'click',
            placement: 'top',
            content: () => renderPopoverList(data, currentIdGetter(), type)
        });

        triggerEl.addEventListener('shown.bs.popover', () => {
            const popoverBody = document.querySelector('.popover-body');
            if (popoverBody) {
                // Listen for clicks on items
                popoverBody.querySelectorAll('.popover-list-item').forEach(item => {
                    item.addEventListener('click', () => {
                        const newId = item.getAttribute('data-id');
                        const newMode = item.getAttribute('data-mode');

                        if (newId) {
                            onSelect(newId);
                        } else if (newMode) {
                            window.chatState.mode = newMode;
                            // Trigger mode change logic if necessary
                            handleModeChange(newMode);
                        }
                        
                        popover.hide();
                    });
                });
            }
        });

        return popover;
    }

    function updatePersonaUI() {
        if (!personaBtn || !personaNameDisplay) return;

        const activePrompt = availablePrompts.find(p => String(getItemId(p)) === String(window.chatState.prompt_id));
        const badge = document.getElementById('activePersonaBadge');

        if (activePrompt) {
            personaBtn.setAttribute('title', `Active Persona: ${activePrompt.name}`);
            personaNameDisplay.textContent = activePrompt.name;
            personaNameDisplay.classList.remove('d-none');

            if (badge) {
                if (activePrompt.is_standard) {
                    badge.classList.add('d-none');
                    personaBtn.classList.remove('text-accent');
                } else {
                    badge.classList.remove('d-none');
                    personaBtn.classList.add('text-accent');
                }
            }
        }
    }

    function updateLlmUI() {
        if (!llmSettingsBtn || !llmNameDisplay) return;

        const activeLlm = availableLlms.find(l => String(getItemId(l)) === String(window.chatState.llm_id));
        const modeLabel = window.chatState.mode === 'analysis' ? 'F' : 'C';

        if (activeLlm) {
            llmSettingsBtn.setAttribute('title', `Active LLM: ${activeLlm.name} (${window.chatState.mode})`);
            llmNameDisplay.textContent = `${activeLlm.name} [${modeLabel}]`;
            llmNameDisplay.classList.remove('d-none');
        }
    }

    function handleModeChange(mode) {
        console.log('Selected Mode:', mode);
        const newChatBtns = document.querySelectorAll('.new-chat-btn');
        
        if (mode === 'conversation') {
            newChatBtns.forEach(btn => btn.style.display = 'block');
        } else {
            newChatBtns.forEach(btn => btn.style.display = 'none');
            resetConversation();
        }
        updateLlmUI();
    }

    // Helper function to append and render a message
    function appendMessageToChat(role, content, isNew = true) {
        const messageElement = document.createElement('div');
        messageElement.classList.add('chat-message', `${role}-message`);
        const messageContentDiv = document.createElement('div');
        messageContentDiv.classList.add('message-content');
        messageContentDiv.innerHTML = DOMPurify.sanitize(marked.parse(content));
        messageElement.appendChild(messageContentDiv);
        
        chatMessages.appendChild(messageElement); // Append first
        renderFormattedContent(messageContentDiv); // Then render KaTeX on the content div

        // Only scroll if it's a new message being added at the end
        if (isNew) {
            chatMessages.scrollTop = chatMessages.scrollHeight;
        }
        return messageElement; // Return the created element if needed
    }



    const frageForm = document.getElementById('frageForm');
    if (!frageForm) return;

    // === DEV_TASKS-0036: Phase 2.1 Initialize State Variables ===
    window.chatState = {
        prompt_id: null,
        llm_id: window.defaultLlmId || null,
        mode: 'analysis' // Default mode
    };

    const frageInput = document.getElementById('frage');
    const sendButton = document.querySelector('#frageForm button[type="submit"]');
    const chatMessages = document.getElementById('chatMessages');
    const loadingIndicator = document.getElementById('loading-indicator');
    const personaBtn = document.getElementById('personaBtn');
    const llmSettingsBtn = document.getElementById('llmSettingsBtn');
    const newChatBtnsClick = document.querySelectorAll('.new-chat-btn');
    const personaNameDisplay = document.getElementById('personaNameDisplay');
    const llmNameDisplay = document.getElementById('llmNameDisplay');

    let availablePrompts = [];
    let availableLlms = [];

    // === Dual Interaction Model State ===
    let currentConversationId = null;
    // ====================================

    let loadingInterval;

    // === Dual Interaction Model Functions ===
    function clearChatDisplay() {
        chatMessages.innerHTML = '';
    }

    function resetConversation() {
        clearChatDisplay();
        currentConversationId = null;
        console.log('New conversation started.');
    }

    function addBotMessage(htmlContent) {
        appendMessageToChat('bot', htmlContent, true);
    }
    // ======================================

    async function loadInitialData() {
        try {
            const [promptRes, llmRes] = await Promise.all([
                axios.get('/api/prompts'),
                llmSettingsBtn ? axios.get('/api/llms') : Promise.resolve({ data: [] })
            ]);

            availablePrompts = promptRes.data;
            const standardPrompt = availablePrompts.find(p => p.is_standard) || availablePrompts[0];
            if (!window.chatState.prompt_id && standardPrompt) {
                window.chatState.prompt_id = getItemId(standardPrompt);
            }

            window.personaPopover = initSelectionPopover(personaBtn, availablePrompts, () => window.chatState.prompt_id, 'prompt', (id) => {
                window.chatState.prompt_id = id;
                updatePersonaUI();
            });
            updatePersonaUI();

            if (llmSettingsBtn) {
                availableLlms = llmRes.data;
                if (availableLlms.length > 0 && !window.chatState.llm_id) {
                    window.chatState.llm_id = getItemId(availableLlms[0]);
                }

                window.llmPopover = initSelectionPopover(llmSettingsBtn, availableLlms, () => window.chatState.llm_id, 'llm', (id) => {
                    window.chatState.llm_id = id;
                    updateLlmUI();
                });
                updateLlmUI();
            }
        } catch (error) {
            console.error('Error loading initial data:', error);
        }
    }

    function startLoadingIndicator() {
        const messages = [
            "Analyzing question...", "Looking something up...",
            "Generating response...", "Formulating response..."
        ];
        let messageIndex = 0;
        loadingIndicator.textContent = messages[messageIndex];
        loadingIndicator.classList.remove('d-none');
        loadingInterval = setInterval(() => {
            messageIndex = (messageIndex + 1) % messages.length;
            loadingIndicator.textContent = messages[messageIndex];
        }, 3000);
    }

    function stopLoadingIndicator() {
        clearInterval(loadingInterval);
        loadingIndicator.classList.add('d-none');
    }

    // === Main Form Submission Handler ===
    // DEV_TASKS-0036: Phase 2.2 Intercept Form Submission
    frageForm.addEventListener('submit', async function (e) {
        e.preventDefault();
        
        // Hide popovers upon sending a message
        if (window.personaPopover) window.personaPopover.hide();
        if (window.llmPopover) window.llmPopover.hide();

        const question = frageInput.value.trim();
        if (!question) return;

        sendButton.disabled = true;
        startLoadingIndicator();

        if (window.chatState.mode === 'analysis') {
            chatMessages.innerHTML = ''; // Clear for analysis mode
        }
        appendMessageToChat('user', question, true); // Append the user message
        frageInput.value = '';

        if (window.chatState.mode === 'analysis') {
            await handleAnalysisRequest(question);
        } else {
            await handleConversationRequest(question);
        }

        stopLoadingIndicator();
        sendButton.disabled = false;
    });

    async function handleAnalysisRequest(question) {
        try {
            const payload = {
                frage: question,
                prompt_id: window.chatState.prompt_id,
                llm_id: window.chatState.llm_id,
                mode: window.chatState.mode
            };

            const response = await axios.post('/ask', payload);

            appendMessageToChat('bot', response.data.antwort, true);

            // Trigger sidebar refresh for pro users
            if (window.isProUser) {
                document.dispatchEvent(new CustomEvent('conversationcreated'));
            }
        } catch (error) {
            const errorHtml = `<div class="chat-message bot-message"><div class="message-content text-danger">An error occurred. Please try again.</div></div>`;
            chatMessages.innerHTML += errorHtml;
            console.error("Error during /ask request:", error);
        }
    }

    async function handleConversationRequest(question) {
        try {
            const isNewConversation = currentConversationId === null;
            
            // Construct payload using JS State
            const payload = {
                message: question,
                conversation_id: currentConversationId,
                llm_id: window.chatState.llm_id,
                prompt_id: window.chatState.prompt_id,
                mode: window.chatState.mode
            };

            const csrfToken = getCookie('csrf_token'); // Get CSRF token from cookie

            const response = await axios.post('/api/conversation/ask', payload, {
                headers: {
                    'X-CSRFToken': csrfToken
                }
            });
            
            appendMessageToChat('bot', response.data.answer, true);

            currentConversationId = response.data.conversation_id;

            // If a new conversation was just created, dispatch an event
            if (isNewConversation) {
                document.dispatchEvent(new CustomEvent('conversationcreated'));
            }

        } catch (error) {
            const errorHtml = `<div class="chat-message bot-message"><div class="message-content text-danger">An error occurred. Please try again.</div></div>`;
            chatMessages.innerHTML += errorHtml;
            console.error("Error during /api/conversation/ask request:", error);
        }
    }

    // Event Listeners Setup (Temporary removal of old modal logic, will be replaced in Phase 4)
    // if (llmSettingsBtn) { ... }

    if (newChatBtnsClick.length > 0) {
        newChatBtnsClick.forEach(btn => btn.addEventListener('click', resetConversation));
    }

    // Listen for the custom event dispatched by history.js
    document.addEventListener('startnewchat', () => {
        resetConversation();
        window.chatState.mode = 'conversation';
    });

    // === DEV_TASKS-0036: Phase 3.4 Handle Popover Selection ===
    document.addEventListener('click', function(e) {
        // Handle Persona Selection
        const personaItem = e.target.closest('.persona-select-item');
        if (personaItem) {
            const newPromptId = personaItem.getAttribute('data-prompt-id');
            if (newPromptId) {
                window.chatState.prompt_id = newPromptId;
                console.log('Selected Persona ID:', window.chatState.prompt_id);
                
                // Update Badge visually
                const badge = document.getElementById('activePersonaBadge');
                const selectedPersona = window.personasData.find(p => getResourceId(p) === newPromptId);
                if (badge && selectedPersona) {
                    if (selectedPersona.is_standard) {
                        badge.classList.add('d-none');
                    } else {
                        badge.textContent = DOMPurify.sanitize(selectedPersona.name);
                        badge.classList.remove('d-none');
                    }
                }
                
                if (window.personaPopover) {
                    window.personaPopover.hide();
                }
            }
        } else {
            // Close popovers when clicking outside
            if (window.personaPopover && personaBtn && !personaBtn.contains(e.target) && !e.target.closest('.popover')) {
                window.personaPopover.hide();
            }
            if (window.llmPopover && llmSettingsBtn && !llmSettingsBtn.contains(e.target) && !e.target.closest('.popover')) {
                window.llmPopover.hide();
            }
        }
    });
    // =================================================

    function populateChatFromData(conversation) {
        if (!conversation || !conversation.messages || conversation.messages.length === 0) {
            console.error("populateChatFromData was called but no messages were found in the conversation object.");
            return;
        }

        clearChatDisplay();
        
        // Robustly get the conversation ID
        currentConversationId = getResourceId(conversation);

        // Set the persona selector if the conversation has a prompt_id
        if (conversation.prompt_id) {
            window.chatState.prompt_id = conversation.prompt_id;
        }

        conversation.messages.forEach(message => {
            appendMessageToChat(message.role, message.content, false);
        });

        chatMessages.scrollTop = chatMessages.scrollHeight;
    }

    // Initial setup
    loadInitialData();

    // Check if conversation data is embedded in the page
    if (window.conversationData) {
        try {
            let conversation = window.conversationData;
            // The data should be an object, but as a safeguard, parse it if it's a string.
            if (typeof conversation === 'string') {
                conversation = JSON.parse(conversation);
            }
            
            populateChatFromData(conversation);
            
            // Switch to conversation mode
            window.chatState.mode = 'conversation';
            
        } catch (e) {
            console.error("Failed to process conversation data:", e);
        }
    } else {
        // Default setup if no conversation is loaded
        const newChatBtns = document.querySelectorAll('.new-chat-btn');
        if (window.chatState.mode !== 'conversation') {
            newChatBtns.forEach(btn => btn.style.display = 'none');
        }
    }

    // Initialize tooltips (except for popover triggers which are handled by initSelectionPopover)
    if (newChatBtnsClick.length > 0) {
        newChatBtnsClick.forEach(btn => new bootstrap.Tooltip(btn));
    }
}